import { useState, useEffect, useRef, useCallback, lazy, Suspense, forwardRef, useImperativeHandle } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { setFocusedEditor } from '@/store/ui/uiSlice'
import TextStatic from '@/components/app/TextStatic/TextStatic'
import css from './TaskModalDescription.module.css'

// Lazy here (not via LazyTextEditor) so we can use the static read view as the Suspense
// fallback — the text stays in place while the editor chunk loads/inits instead of
// collapsing to empty, which is what made the swap jump on click.
const TextEditor = lazy(() => import('@/components/app/TextEditor/TextEditor'))

const EDITOR_ID = 'task-description'

interface TaskModalDescriptionProps {
    description: unknown
    onSave: (description: unknown) => Promise<void>
    onFileUpload?: (file: File) => Promise<{ id: string; url: string }>
}

export interface TaskModalDescriptionHandle {
    // Append files to the description (entering edit mode so the uploading placeholders are
    // visible). Returns false when the drop can't be accepted yet — description not loaded,
    // or no upload callback (signed-out).
    insertFiles: (files: File[]) => boolean
}

// Read view by default (no live editor mounted), swapping to the real editor only while
// editing. Mounting a Plate editor for every open task is the cold-open cost this avoids.
const TaskModalDescription = forwardRef<TaskModalDescriptionHandle, TaskModalDescriptionProps>(({ description, onSave, onFileUpload }, ref) => {
    const dispatch = useAppDispatch()
    const [editing, setEditing] = useState(false)
    // Mount the editor (hidden) once the pointer enters, so spinning up the Plate instance
    // happens during the hover→click gap instead of adding lag to the click itself.
    const [prewarm, setPrewarm] = useState(false)
    // Viewport point of the click that started editing, so the editor places the caret there
    // instead of jumping to the end (the static read view and editor lay out identically).
    const [focusPoint, setFocusPoint] = useState<{ x: number; y: number } | null>(null)
    // Mirror the prop so the static view shows just-typed content immediately after blur,
    // without waiting for the Firestore round-trip to flow back through props.
    const [localValue, setLocalValue] = useState(description)
    // Adopt the prop ONLY on a genuine external change (e.g. agent/another tab edited it) —
    // not on the editing→false toggle, which would briefly flash the pre-save content while
    // our own write is still round-tripping through Firestore.
    const lastDescRef = useRef(description)
    useEffect(() => {
        if (JSON.stringify(description) === JSON.stringify(lastDescRef.current)) return
        lastDescRef.current = description
        if (!editing) setLocalValue(description)
    }, [description, editing])

    const startEditing = (e: React.MouseEvent) => {
        // Only skip entering edit for elements with their own click action in the read view:
        // links / the file download link (`a`), buttons, and the Figma preview (opens a
        // lightbox). A plain image has no read action, so clicking it enters edit so it can be
        // selected/deleted like the rest of the content.
        if ((e.target as HTMLElement).closest('a, button, [data-lightbox-trigger]')) return
        // Don't hijack a text selection: the click that ends a drag-select (to copy text) must not
        // enter edit mode and collapse the selection. A plain click leaves the selection collapsed
        // and still enters edit (SCD-896).
        const selection = window.getSelection()
        if (selection && !selection.isCollapsed && selection.toString().trim() !== '') return
        // Use click, not mousedown: the modal closes on a document `mousedown` whose target is
        // outside the modal, and swapping the static node for the editor synchronously on
        // mousedown detaches the clicked node before that check runs, so it reads as "outside"
        // and the modal closes. On click the mousedown check has already passed.
        setFocusPoint({ x: e.clientX, y: e.clientY })
        dispatch(setFocusedEditor(EDITOR_ID))
        setEditing(true)
    }

    const stopEditing = () => {
        dispatch(setFocusedEditor(null))
        setFocusPoint(null)
        setEditing(false)
    }

    // The lazy editor's append-files function (registered when it mounts). Files handed to
    // insertFiles before the editor is ready are queued and flushed on registration.
    const editorInsertRef = useRef<((files: File[]) => void) | null>(null)
    const pendingFilesRef = useRef<File[]>([])
    const handleExternalInsertReady = useCallback((insert: ((files: File[]) => void) | null) => {
        editorInsertRef.current = insert
        if (insert && pendingFilesRef.current.length) {
            const queued = pendingFilesRef.current
            pendingFilesRef.current = []
            insert(queued)
        }
    }, [])

    useImperativeHandle(ref, () => ({
        insertFiles: (files: File[]) => {
            if (localValue === undefined || files.length === 0 || !onFileUpload) return false
            // Enter edit mode like a drag over the description does — the editor shows the
            // uploading placeholders and its blur flow saves the result.
            setPrewarm(true)
            setFocusPoint(null)
            setEditing(true)
            if (editorInsertRef.current) editorInsertRef.current(files)
            else pendingFilesRef.current.push(...files)
            return true
        }
    }))

    // Dragging a file onto the (read-only) description enters edit mode, so the editor's own
    // drop handler does the upload/insert — no duplicated logic. The editor mounts during the
    // drag; by the time the file is released its capture-phase drop listener is attached and
    // consumes the drop (before the safety onDrop below).
    const isFileDrag = (e: React.DragEvent) => e.dataTransfer.types.includes('Files')
    const handleDragEnter = (e: React.DragEvent) => {
        // Don't enter edit before the description has loaded — the editor would mount with an
        // undefined value. (handleDragOver/handleDrop still preventDefault so the browser
        // doesn't navigate to the file in that brief window.)
        if (editing || localValue === undefined || !isFileDrag(e)) return
        setPrewarm(true)
        setFocusPoint(null)
        setEditing(true)
    }
    const handleDragOver = (e: React.DragEvent) => {
        if (isFileDrag(e)) e.preventDefault() // keep the drag valid so a drop fires
    }
    const handleDrop = (e: React.DragEvent) => {
        // Safety net only: the editor normally consumes the drop first. If it hadn't mounted
        // yet, stop the browser from navigating to the dropped file.
        if (isFileDrag(e)) e.preventDefault()
    }

    // The read view. TextStatic renders the placeholder (as the editor's exact placeholder DOM)
    // when empty, so it's used both as read mode and as the editor's Suspense fallback —
    // entering edit on an empty description keeps the same placeholder element in place, with no
    // flicker and no swap to a different element.
    const renderReadView = () => <TextStatic value={localValue} placeholder="Add a description..." />

    // `undefined` = the description doc hasn't arrived yet (it loads lazily, separate from the
    // task). Reserve the space and show nothing — no skeleton, no placeholder, not interactive —
    // until it's actually loaded ([] or content).
    const notLoaded = localValue === undefined
    if (notLoaded) {
        // Still swallow file drops so a drop during the brief load window doesn't navigate away.
        return <div className={css.editor} onDragOver={handleDragOver} onDrop={handleDrop} />
    }

    const showEditor = prewarm || editing

    return (
        <div
            className={css.editor}
            data-testid="task-description"
            onMouseEnter={() => setPrewarm(true)}
            onClick={editing ? undefined : startEditing}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {showEditor && (
                // One editor instance: hidden while pre-warming, revealed on edit. `active`
                // gates focus so the hidden instance doesn't steal focus before the click.
                <div className={editing ? undefined : css.prewarm}>
                    <Suspense fallback={editing ? renderReadView() : null}>
                        <TextEditor
                            value={localValue}
                            placeholder="Add a description..."
                            autoFocus
                            active={editing}
                            focusAtPoint={focusPoint}
                            onSave={onSave}
                            onChangeImmediate={setLocalValue}
                            onFileUpload={onFileUpload}
                            onBlur={stopEditing}
                            onExternalInsertReady={handleExternalInsertReady}
                        />
                    </Suspense>
                </div>
            )}
            {!editing && renderReadView()}
        </div>
    )
})

export default TaskModalDescription
